import { ASSET_TYPES } from "../../../enums/assetTypes.enum";
import { isNotNullOrUndefined } from "../../../shared/utils/utils";
import { Address } from "../../shared/address";
import { Position } from "../../shared/position";
import { LocationRef, CustomerRef, Person, TenantRef, AtedBy, Timestamp, Reference } from "../../shared/refs";
import { Parents } from "./botevoAsset";

export interface BuildingDTO {
  id?: string;
  assetType?: string;
  label: string;
  email?: string | null;
  phone?: string | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  surface?: number | null;
  volume?: number | null;
  noOfGates?: number;
  floorMapUrl?: string;
  address?: Address;
  location?: LocationRef;
  customer?: CustomerRef;
  position?: Position | null;
  severity?: string;
  gateArea?: null;
  numberOfGateSystems?: number;
  ecoEmployee?: Person;
  techEmployee?: Person;
  totalGateSurface?: number | null;
  energyCost?: number | null;
  expectedEnergyLossPerDay?: number;
  expectedEnergyLossPerYear?: number;
  totalGates?: number | null;
  climateType?: string | null;
  timestamp?: Timestamp;
  tenant?: TenantRef;
  createdBy?: AtedBy;
  updatedBy?: AtedBy;
  notes?: string | null;
}

export class Building {
  id?: string;
  label: string;
  email?: string;
  phone?: string;
  notes?: string;
  address?: Address;
  position?: Position;
  length?: number;
  height?: number;
  width?: number;
  surface?: number;
  volume?: number;
  numberOfGateSystems?: number;
  customer?: Reference;
  techEmployee?: Person;
  ecoEmployee?: Person;
  totalGateSurface?: number;
  energyCost?: number;
  totalGates?: number;
  climateType?: string;
  floorMapUrl?: string;
  assetType: string;
  location?: LocationRef;
  expectedEnergyLossPerDay?: number;

  constructor(building: BuildingDTO) {
    this.id = building.id;
    this.label = building.label;
    this.customer = building.customer;
    if (building.email) this.email = building.email;
    if (building.phone) this.phone = building.phone;
    if (building.notes) this.notes = building.notes;
    this.address = building.address;
    if (building.position) this.position = building.position;
    this.location = building.location;
    if (isNotNullOrUndefined(building.length)) this.length = building.length;
    if (isNotNullOrUndefined(building.height)) this.height = building.height;
    if (isNotNullOrUndefined(building.width)) this.width = building.width;
    if (isNotNullOrUndefined(building.surface)) this.surface = building.surface;
    if (isNotNullOrUndefined(building.volume)) this.volume = building.volume;
    this.floorMapUrl = building.floorMapUrl;
    this.numberOfGateSystems = building.numberOfGateSystems;
    this.assetType = ASSET_TYPES.BUILDING;
    this.techEmployee = building.techEmployee;
    this.ecoEmployee = building.ecoEmployee;
    if (isNotNullOrUndefined(building.totalGateSurface)) this.totalGateSurface = building.totalGateSurface;
    if (isNotNullOrUndefined(building.energyCost)) this.energyCost = building.energyCost;
    if (isNotNullOrUndefined(building.totalGates)) this.totalGates = building.totalGates;
    this.climateType = building.climateType ? building.climateType : "heating";
    this.expectedEnergyLossPerDay =
      building.expectedEnergyLossPerYear !== undefined ? building.expectedEnergyLossPerYear / 365 : undefined;
  }

  public set floorMap(floorMapUrl: string) {
    this.floorMapUrl = floorMapUrl;
  }

  public set parents(parents: Parents) {
    if (parents.location) this.location = parents.location;
    if (parents.customer) this.customer = parents.customer;
  }

  public set properties(building: BuildingDTO) {
    if (building.label) this.label = building.label;
    if (isNotNullOrUndefined(building.email)) this.email = building.email;
    if (isNotNullOrUndefined(building.phone)) this.phone = building.phone;
    if (isNotNullOrUndefined(building.notes)) this.notes = building.notes;
    if (building.address) this.address = building.address;
    if (isNotNullOrUndefined(building.height)) this.height = building.height;
    if (isNotNullOrUndefined(building.width)) this.width = building.width;
    if (isNotNullOrUndefined(building.length)) this.length = building.length;
    if (building.position) this.position = building.position;
    this.techEmployee = building.techEmployee;
    this.ecoEmployee = building.ecoEmployee;
    if (isNotNullOrUndefined(building.totalGateSurface)) this.totalGateSurface = building.totalGateSurface;
    if (isNotNullOrUndefined(building.energyCost)) this.energyCost = building.energyCost;
    if (isNotNullOrUndefined(building.totalGates)) this.totalGates = building.totalGates;
    if (building.climateType) this.climateType = building.climateType;
  }

  public get type() {
    return this.assetType;
  }

  public get directParentId() {
    return this.location?.id;
  }

  public get values() {
    const building: BuildingDTO = {
      label: this.label,
      location: { id: this.location?.id },
    };
    if (this.customer) {
      building.customer = this.customer;
    }
    if (this.address) {
      building.address = this.address;
      if (!this.address.street) {
        delete building.address.street;
      }
      if (!this.address.number) {
        delete building.address.number;
      }
      if (!this.address.city) {
        delete building.address.city;
      }
      if (!this.address.country) {
        delete building.address.country;
      }
      if (!this.address.zipCode) {
        delete building.address.zipCode;
      }
    }

    if (this.floorMapUrl) {
      building.floorMapUrl = this.floorMapUrl;
    }
    if (isNotNullOrUndefined(this.email)) {
      building.email = this.email;
    }
    if (isNotNullOrUndefined(this.phone)) {
      building.phone = this.phone;
    }
    if (this.position && this.position.latitude && this.position.longitude) {
      building.position = this.position;
    }
    if (this.techEmployee && (this.techEmployee.name || this.techEmployee.email || this.techEmployee.phone)) {
      {
        building.techEmployee = this.techEmployee;
        if (!this.techEmployee.name) {
          delete building.techEmployee.name;
        }
        if (!this.techEmployee.email) {
          delete building.techEmployee.email;
        }
        if (!this.techEmployee.phone) {
          delete building.techEmployee.phone;
        }
      }
    }

    if (this.ecoEmployee && (this.ecoEmployee.name || this.ecoEmployee.email || this.ecoEmployee.phone)) {
      {
        building.ecoEmployee = this.ecoEmployee;
        if (!this.ecoEmployee.name) {
          delete building.ecoEmployee.name;
        }
        if (!this.ecoEmployee.email) {
          delete building.ecoEmployee.email;
        }
        if (!this.ecoEmployee.phone) {
          delete building.ecoEmployee.phone;
        }
      }
    }

    if (this.totalGateSurface) {
      building.totalGateSurface = this.totalGateSurface;
    }

    if (this.energyCost) {
      building.energyCost = this.energyCost;
    }

    if (this.totalGates) {
      building.totalGates = this.totalGates;
    }

    if (this.climateType) {
      building.climateType = this.climateType;
    }

    if (isNotNullOrUndefined(this.notes)) {
      building.notes = this.notes;
    }
    if (isNotNullOrUndefined(this.height)) {
      building.height = this.height;
    }
    if (isNotNullOrUndefined(this.width)) {
      building.width = this.width;
    }
    if (isNotNullOrUndefined(this.length)) {
      building.length = this.length;
    }

    return building;
  }
}
